import { USAGE_ACTION } from '../constants/sample.constants';
import type { UsageRecordDayRow } from '../services/report.service';
import {
  buildSampleLocationFacts,
  joinSampleLocationFacts,
} from './sampleLocationDisplay';
import { joinLabeledValue } from './joinLabeledValue';
import {
  formatSampleQuantityWithUnit,
  SAMPLE_DEFAULT_UNIT,
} from './sampleUnitLabels';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function formatUsageRecordTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function formatUsageRecordLocation(row: UsageRecordDayRow): string {
  return joinSampleLocationFacts(
    buildSampleLocationFacts({
      storageRoom: row.storageRoomName,
      drawerNumber: row.drawerNumber,
      caseName: row.caseName,
    })
  );
}

export function formatUsageRecordDetail(row: UsageRecordDayRow): string {
  return (row.purpose || row.notes || '').trim() || '-';
}

function joinFacts(parts: Array<string | null | undefined>): string {
  return parts.filter((p): p is string => Boolean(p && String(p).trim())).join(' · ');
}

/** xs: 操作・数量・残を1行に（場所・メモは別行） */
export function formatUsageRecordMobilePrimary(
  row: UsageRecordDayRow,
  quantityZeroLabel: string,
  t: Translate
): string {
  const qty =
    row.action === USAGE_ACTION.CREATED
      ? null
      : row.quantity === 0
        ? quantityZeroLabel
        : row.quantity == null
          ? null
          : formatSampleQuantityWithUnit(row.quantity, row.unit, t);
  const remain =
    row.remainingQuantity == null
      ? null
      : joinLabeledValue(
          t('sample.remaining_quantity_short', { defaultValue: '残' }),
          formatSampleQuantityWithUnit(row.remainingQuantity, row.unit, t)
        );
  return joinFacts([row.action || null, qty, remain]);
}

export function computeUsageRecordDaySummary(records: UsageRecordDayRow[]) {
  const qtyRows = records.filter(
    (r) => r.action === USAGE_ACTION.USE || r.action === USAGE_ACTION.DISPOSE
  );
  const usedQuantity = qtyRows.reduce((sum, r) => sum + (r.quantity ?? 0), 0);
  const unitKeys = new Set(
    qtyRows.map((r) => (r.unit?.trim() || SAMPLE_DEFAULT_UNIT).toLowerCase())
  );
  /** 合算が意味を持つ同一単位のときだけ。混在は null（数値のみ表示） */
  const sharedUnit =
    qtyRows.length > 0 && unitKeys.size === 1
      ? qtyRows[0]?.unit?.trim() || SAMPLE_DEFAULT_UNIT
      : null;
  const uniqueSampleCount = new Set(records.map((r) => r.sampleId)).size;
  return {
    recordCount: records.length,
    uniqueSampleCount,
    usedQuantity,
    sharedUnit,
  };
}

/** 日次合算数量の表示値（「3 本」または単位混在時の「3」） */
export function formatDayUsedQuantityValue(
  usedQuantity: number,
  sharedUnit: string | null,
  t: Translate
): string {
  return sharedUnit != null
    ? formatSampleQuantityWithUnit(usedQuantity, sharedUnit, t)
    : String(usedQuantity);
}

/** 日次ヘッダ用「使用 3 本」／単位混在時は「使用 3」 */
export function formatDayUsedQuantityLabel(
  usedQuantity: number,
  sharedUnit: string | null,
  t: Translate
): string {
  const quantity = formatDayUsedQuantityValue(usedQuantity, sharedUnit, t);
  return t('usage_records.day_used_quantity', {
    count: usedQuantity,
    quantity,
    defaultValue: joinLabeledValue(
      t('usage_record.action_use', { defaultValue: '使用' }),
      quantity
    ),
  });
}

/**
 * 履歴の一括展開／折りたたみ。
 * 1日でも開いていれば全閉、すべて閉じていれば全日開。
 * （全展開時だけ折りたたみ表示だと、初期の1日展開時に折りたたみが選べない）
 */
export function nextExpandedDaysOnToggleAll(
  dayDates: readonly string[],
  expandedDays: ReadonlySet<string>
): Set<string> {
  if (expandedDays.size > 0) return new Set();
  return new Set(dayDates);
}
